// src/features/orders/components/OrdersHeader.tsx
import clsx from 'clsx';
import type { Order } from '../types/orders.types';

interface OrdersHeaderProps {
  orders: Order[];
  isLoading?: boolean;
}

interface StatusCounts {
  completed: number;
  pending: number;
  cancelled: number;
}

function countByStatus(orders: Order[]): StatusCounts {
  return orders.reduce<StatusCounts>(
    (acc, order) => {
      acc[order.status] += 1;
      return acc;
    },
    { completed: 0, pending: 0, cancelled: 0 },
  );
}

// Compact chip styling, reusing the same token-driven pattern as
// OrderStatusBadge. "total" has no accent mapping (none was specified),
// so it stays neutral using existing text/border tokens.
const CHIP_BASE =
  'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-medium';

const CHIP_TONE = {
  neutral:
    'text-[var(--vania-colors-text-secondary,#a3aab8)] bg-[var(--vania-colors-border-subtle,rgba(255,255,255,0.08))] border-[var(--vania-colors-border-subtle,rgba(255,255,255,0.08))]',
  success: 'text-[var(--vania-accent-teal)] bg-[var(--vania-accent-teal)]/10 border-[var(--vania-accent-teal)]/20',
  warning: 'text-[var(--vania-accent-amber)] bg-[var(--vania-accent-amber)]/10 border-[var(--vania-accent-amber)]/20',
  danger: 'text-[var(--vania-accent-rose)] bg-[var(--vania-accent-rose)]/10 border-[var(--vania-accent-rose)]/20',
} as const;

interface SummaryChipProps {
  tone: keyof typeof CHIP_TONE;
  label: string;
}

function SummaryChip({ tone, label }: SummaryChipProps) {
  return <span className={clsx(CHIP_BASE, CHIP_TONE[tone])}>{label}</span>;
}

/**
 * Displays the Orders page title plus a real-time summary derived entirely
 * from the `orders` array already loaded by useOrders. No values here are
 * invented — everything is a direct count over the existing data set.
 */
export function OrdersHeader({ orders, isLoading = false }: OrdersHeaderProps) {
  const counts = countByStatus(orders);

  return (
    <div className="flex flex-col gap-2">
      <h1 className="text-lg font-semibold text-[var(--vania-colors-text-primary,#f5f6fa)]">
        سفارش‌ها
      </h1>

      {!isLoading && (
        <div className="flex flex-wrap items-center gap-1.5">
          <SummaryChip tone="neutral" label={`${orders.length} سفارش`} />
          <SummaryChip tone="success" label={`${counts.completed} موفق`} />
          <SummaryChip tone="warning" label={`${counts.pending} در انتظار`} />
          <SummaryChip tone="danger" label={`${counts.cancelled} لغو شده`} />
        </div>
      )}
    </div>
  );
}
